import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SaveIndicator } from "@/components/shared/save-indicator";
import { useAutoSave } from "@/hooks/use-auto-save";
import { useUpdateObjective, useObjectives } from "@/hooks/use-objectives";
import { isTerminalStatus } from "@/lib/status-machine";
import { primaryOf, countMultiAlignmentItems } from "@/lib/alignment-helpers";
import { AlignmentFields, isParentValidForLevel } from "./alignment-fields";
import { ObjectiveTypeSelect } from "./objective-type-field";
import type { ObjectiveLevel } from "@/lib/objective-levels";
import type { Objective, UpdateObjectiveRequest } from "@/types/entities";
import { YEAR_OPTIONS } from "@/lib/year-options";

function getInitialAlignmentIds(objective: Objective) {
  return {
    parent: primaryOf(objective.parent_objective_ids),
    focusArea: primaryOf(objective.focus_area_ids),
    tactic: primaryOf(objective.tactic_ids),
  };
}

interface ObjectiveInlineEditorProps {
  objective: Objective;
  canEdit: boolean;
  isDepartmentHead?: boolean;
}

export function ObjectiveInlineEditor({ objective, canEdit, isDepartmentHead = false }: ObjectiveInlineEditorProps) {
  const [title, setTitle] = useState(objective.title);
  const [description, setDescription] = useState(objective.description);
  const updateObjective = useUpdateObjective(objective.id);
  const { saveStatus, debouncedSave, save } = useAutoSave<UpdateObjectiveRequest>({ mutation: updateObjective });

  const isEditable = canEdit && !isTerminalStatus(objective.status);

  // Alignment state
  const initial = getInitialAlignmentIds(objective);
  const [parentObjectiveId, setParentObjectiveId] = useState<string | null>(initial.parent);
  const [focusAreaId, setFocusAreaId] = useState<string | null>(initial.focusArea);
  const [tacticId, setTacticId] = useState<string | null>(initial.tactic);

  const { data: allObjectivesData } = useObjectives({});

  useEffect(() => {
    setTitle(objective.title);
    setDescription(objective.description);
    setParentObjectiveId(primaryOf(objective.parent_objective_ids));
    setFocusAreaId(primaryOf(objective.focus_area_ids));
    setTacticId(primaryOf(objective.tactic_ids));
  }, [objective.title, objective.description, objective.parent_objective_ids, objective.focus_area_ids, objective.tactic_ids]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (value.trim()) {
      debouncedSave({ title: value });
    }
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    debouncedSave({ description: value });
  };

  const handleYearChange = (value: string) => {
    save({ year: parseInt(value, 10) });
  };

  const handleTypeChange = (value: "committed" | "aspirational") => {
    save({ type: value });
  };

  const handleLevelChange = useCallback(
    (value: string) => {
      const newLevel = value as ObjectiveLevel;
      if (!isParentValidForLevel(parentObjectiveId, newLevel, allObjectivesData?.data ?? [])) {
        setParentObjectiveId(null);
        save({ level: newLevel, parent_objective_ids: [] });
      } else {
        save({ level: newLevel });
      }
    },
    [parentObjectiveId, allObjectivesData, save],
  );

  const handleParentObjectiveIdChange = useCallback(
    (id: string | null) => {
      setParentObjectiveId(id);
      save({ parent_objective_ids: id ? [id] : [] });
    },
    [save],
  );

  const handleFocusAreaIdChange = useCallback(
    (id: string | null) => {
      setFocusAreaId(id);
      save({ focus_area_ids: id ? [id] : [] });
    },
    [save],
  );

  const handleTacticIdChange = useCallback(
    (id: string | null) => {
      setTacticId(id);
      save({ tactic_ids: id ? [id] : [] });
    },
    [save],
  );

  const multiAlignmentCount = countMultiAlignmentItems(
    objective.parent_objective_ids,
    objective.focus_area_ids,
    objective.tactic_ids,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label htmlFor="obj-title" className="text-sm font-medium">
          Title
        </Label>
        <SaveIndicator status={saveStatus} />
      </div>

      {isEditable ? (
        <Input
          id="obj-title"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
        />
      ) : (
        <p className="text-lg font-semibold">{title}</p>
      )}

      <div className="space-y-2">
        <Label htmlFor="obj-desc">Description</Label>
        {isEditable ? (
          <Textarea
            id="obj-desc"
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            rows={4}
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm text-[hsl(var(--muted-foreground))]">
            {description || "No description"}
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {isEditable ? (
          <ObjectiveTypeSelect
            value={objective.type}
            onChange={handleTypeChange}
          />
        ) : (
          <div className="space-y-2">
            <Label>Type</Label>
            <p className="text-sm capitalize">{objective.type}</p>
          </div>
        )}

        <div className="space-y-2">
          <Label>Level</Label>
          {isEditable ? (
            <Select value={objective.level} onValueChange={handleLevelChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="department">Department</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm capitalize">{objective.level}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Year</Label>
          {isEditable ? (
            <Select value={objective.year.toString()} onValueChange={handleYearChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm">{objective.year}</p>
          )}
        </div>
      </div>

      <AlignmentFields
        level={objective.level as ObjectiveLevel}
        parentObjectiveId={parentObjectiveId}
        focusAreaId={focusAreaId}
        tacticId={tacticId}
        onParentObjectiveIdChange={handleParentObjectiveIdChange}
        onFocusAreaIdChange={handleFocusAreaIdChange}
        onTacticIdChange={handleTacticIdChange}
        isDepartmentHead={isDepartmentHead}
        excludeObjectiveId={objective.id}
        disabled={!isEditable}
      />
      {multiAlignmentCount > 0 && (
        <p className="text-sm italic text-[hsl(var(--muted-foreground))]">
          Previously aligned to {multiAlignmentCount} items; save to consolidate.
        </p>
      )}
    </div>
  );
}
